import { useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { TextInput } from '@shared/uikit/input-text';
import { formatNum } from '@app/utils/math';

import { X } from 'lucide-react';
import { getCurrentQuarter } from '../../../../shared/utils/getCurrentQuarter';
import { TextAlign, TextSize, Typography } from '@shared/index';
import BaseButton from '@shared/uikit/base-button';

export interface QuarterBonus {
  id?: number | null;
  year: number;
  quarter: number;
  plan: number;
  hashrate: number;
  percentage_change: number;
}

type Props = {
  quarterlyBonus: Array<QuarterBonus> | null;
  open: boolean;
  onClose: VoidFunction;
  onSubmit: (quarters: { quarter: number; plan: string }[]) => Promise<void>;
  year: number;
};

type QuarterValues = Record<number, string>;
const EMPTY_QUARTERS: QuarterValues = { 1: '', 2: '', 3: '', 4: '' };

export const QuarterlyBonusEditPopup = ({
  quarterlyBonus,
  open,
  onClose,
  onSubmit,
  year,
}: Props) => {
  const [quarters, setQuarters] = useState<QuarterValues>(EMPTY_QUARTERS);

  const { currentYear, currentQuarter } = useMemo(() => {
    return {
      currentYear: new Date().getFullYear(),
      currentQuarter: getCurrentQuarter(),
    };
  }, []);

  const isQuarterPassed = (quarter: number) =>
    year < currentYear || (year === currentYear && quarter < currentQuarter);

  useEffect(() => {
    if (!open) {
      setQuarters(EMPTY_QUARTERS);
      return;
    }

    if (quarterlyBonus) {
      const values = { ...EMPTY_QUARTERS };
      quarterlyBonus.forEach(item => {
        if (item.plan != null) {
          values[item.quarter] = formatNum(item.plan);
        }
      });
      setQuarters(values);
    }
  }, [open, quarterlyBonus]);

  const handleQuarterChange = (quarter: number, value: string) => {
    setQuarters(prev => ({
      ...prev,
      [quarter]: value,
    }));
  };

  const handleSubmit = async () => {
    const quartersToSubmit = Object.entries(quarters)
      .map(([quarterString, value]) => ({
        quarter: Number(quarterString),
        value: value.trim(),
      }))
      .filter(item => {
        if (isQuarterPassed(item.quarter)) return false;
        return item.value !== '';
      })
      .map(item => ({
        quarter: item.quarter,
        plan: item.value.replace(/\s/g, '').replace(',', '.'),
      }));

    await onSubmit(quartersToSubmit);
    onClose();
  };

  return (
    <Dialog.Root open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-1000" />
        <Dialog.Content
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-dark-300 rounded-[10px] z-[9999] shadow-lg"
          style={{
            width: '400px',
            maxWidth: '90vw',
            padding: '24px',
          }}
          onEscapeKeyDown={onClose}
          onPointerDownOutside={onClose}
        >
          <Dialog.Close className="absolute top-4 right-4 cursor-pointer" onClick={onClose}>
            <X size={20} className="text-white" />
          </Dialog.Close>
          <Typography
            title="Установить квартальный бонус"
            size={TextSize.size18}
            align={TextAlign.CENTER}
            className="font-semibold text-white mb-5"
          />
          <div className="flex flex-col gap-[15px] mb-[40px]">
            {[1, 2, 3, 4].map(quarter => {
              const isPassed = isQuarterPassed(quarter);

              return (
                <div key={quarter} className="w-full flex items-center gap-2">
                  <div
                    className={`text-[14px] whitespace-nowrap ${isPassed ? 'text-gray-500' : 'text-gray-400'}`}
                  >
                    Q{quarter}
                  </div>
                  <div className="relative flex-1">
                    <TextInput
                      width="100%"
                      value={quarters[quarter]}
                      borderColor="#373539"
                      onChange={value => handleQuarterChange(quarter, value)}
                      placeholder="Введите бонус"
                      decimalFormattingMode={true}
                      disabled={isPassed}
                    />
                    <span
                      className={`absolute top-1/2 -translate-y-1/2 right-[15px] text-[12px] z-[5px] ${isPassed ? 'text-gray-600' : 'text-gray-400'}`}
                    >
                      Ph
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex flex-col items-center gap-5 mb-8">
            <BaseButton
              onClick={handleSubmit}
              className="bg-gray-800 px-6 p-[10px] w-auto h-auto border-none min-w-[174px] whitespace-nowrap"
            >
              <Typography
                text="Сохранить изменения"
                size={TextSize.size14}
                className="text-white font-medium"
              />
            </BaseButton>

            <div className="cursor-pointer" onClick={onClose}>
              <Typography
                text="Отменить"
                size={TextSize.size10}
                className="font-medium text-white/30"
              />
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
